import { useMutation, useQuery } from "@apollo/client";
import { CONFIG_DEFAULTS, OutcomeTrackingMode } from "@salon/types";
import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { gql } from "@/gql";
import { useConfigurationSubscription } from "@/hooks/use-configuration-subscription";
import { useSalonStore } from "@/lib/store/salon";
import type { PersistField } from "../../../hooks/use-auto-save-field";
import { useAutoSaveField } from "../../../hooks/use-auto-save-field";
import { normalizeOutcomeTrackingMode } from "../utils/outcome-tracking-enums";

const GET_SALON_CONFIGS = gql(`
  query GetSalonConfigurations($salonId: ID!, $category: String) {
    getSalonConfigurations(salonId: $salonId, filters: { category: $category }) {
      configurations {
        key
        value
        dataType
        category
        createdAt
        updatedAt
      }
    }
  }
`);

const UPDATE_SALON_CONFIGURATIONS = gql(`
  mutation UpdateSalonConfigurations($salonId: ID!, $input: UpdateSalonConfigurationsInput!) {
    updateSalonConfigurations(salonId: $salonId, input: $input) {
      key
      value
      dataType
      category
      updatedAt
    }
  }
`);

export interface SalonConfig {
  appointmentWorkerAssignmentEnabled: boolean;
  outcomeTrackingEnabled: boolean;
  outcomeTrackingMode: OutcomeTrackingMode;
}
export function useSalonConfig() {
  const { currentSalon } = useSalonStore();
  const selectedSalonId = currentSalon?.id;
  const [saving, setSaving] = useState(false);
  const [mutationError, setMutationError] = useState<Error | null>(null);

  // Subscribe to real-time configuration updates so changes from Partner app
  // (or another Dashboard tab) are reflected without requiring a page reload.
  useConfigurationSubscription({
    salonId: selectedSalonId ?? "",
    categories: ["salon"],
    enabled: !!selectedSalonId,
  });

  const {
    data,
    loading: isLoading,
    error,
  } = useQuery(GET_SALON_CONFIGS, {
    variables: {
      salonId: selectedSalonId ?? "",
      category: "salon",
    },
    skip: !selectedSalonId,
  });

  const [updateConfigsMutation] = useMutation(UPDATE_SALON_CONFIGURATIONS);

  // Parse configurations into typed object. Fallbacks come from the canonical
  // CONFIG_DEFAULTS in @salon/types — do not hardcode here, keep parity with
  // backend / Partner app via the shared package.
  const configurations = data?.getSalonConfigurations?.configurations || [];
  const defaultValues: SalonConfig = useMemo(
    () => ({
      appointmentWorkerAssignmentEnabled: parseConfigValue(
        configurations,
        "salon.appointment_worker_assignment_enabled",
        "boolean",
        CONFIG_DEFAULTS["salon.appointment_worker_assignment_enabled"]
      ),
      outcomeTrackingEnabled: parseConfigValue(
        configurations,
        "salon.outcome_tracking_enabled",
        "boolean",
        CONFIG_DEFAULTS["salon.outcome_tracking_enabled"]
      ),
      outcomeTrackingMode: parseConfigValue(
        configurations,
        "salon.outcome_tracking_mode",
        "outcome_tracking_mode",
        CONFIG_DEFAULTS["salon.outcome_tracking_mode"]
      ),
    }),
    [configurations]
  );

  // Use `values` so RHF auto-syncs when Apollo cache updates (initial load,
  // local refetch, or subscription push). `keepDirtyValues` preserves edits
  // the user has in-flight while still updating untouched fields.
  const form = useForm<SalonConfig>({
    values: defaultValues,
    resetOptions: { keepDirtyValues: true },
    mode: "onChange",
  });

  const persistField: PersistField<SalonConfig> = useCallback(
    async (name, value) => {
      if (!selectedSalonId) {
        throw new Error("No salon selected");
      }
      const dataType = getDataTypeForKey(name);
      return updateConfigsMutation({
        variables: {
          salonId: selectedSalonId,
          input: {
            configurations: [
              {
                key: `salon.${camelToSnakeCase(name)}`,
                value: serializeValue(value, dataType),
                dataType,
              },
            ],
          },
        },
        refetchQueries: [GET_SALON_CONFIGS],
      });
    },
    [selectedSalonId, updateConfigsMutation]
  );
  const { saveField, savingFields } = useAutoSaveField(form, persistField);

  // Save function
  const save = useCallback(async () => {
    if (!form.formState.isDirty || !selectedSalonId) {
      toast.info("No changes to save");
      return;
    }

    setSaving(true);
    setMutationError(null);

    try {
      const formData = form.getValues();

      // Build configurations array for bulk update
      const configurations = Object.keys(formData).map((key) => {
        const configKey = `salon.${camelToSnakeCase(key)}`;
        const dataType = getDataTypeForKey(key as keyof SalonConfig);
        const stringValue = serializeValue(formData[key as keyof SalonConfig], dataType);

        return {
          key: configKey,
          value: stringValue,
          dataType,
        };
      });

      // Single bulk update mutation
      await updateConfigsMutation({
        variables: {
          salonId: selectedSalonId,
          input: { configurations },
        },
        refetchQueries: [GET_SALON_CONFIGS],
      });

      // Mark form as pristine after successful save
      // Don't reset values - let user continue interacting normally
      form.reset(form.getValues(), { keepValues: true });
      toast.success("Salon configuration saved successfully");
    } catch (error) {
      console.error("Salon configuration save error:", error);
      const mutationErr = error instanceof Error ? error : new Error("Save failed");
      setMutationError(mutationErr);
      toast.error(`Save failed: ${mutationErr.message}`);
      throw error;
    } finally {
      setSaving(false);
    }
  }, [form, selectedSalonId, updateConfigsMutation]);

  // Discard function
  const discard = useCallback(() => {
    form.reset(defaultValues);
    setMutationError(null);
    toast.info("Changes discarded");
  }, [form, defaultValues]);

  const combinedError = error || mutationError;

  return {
    form,
    config: form.watch(), // Return current form values
    isLoading,
    saving,
    error: combinedError,
    save,
    discard,
    saveField,
    savingFields,
  };
}

// Helper functions
function parseConfigValue<T>(
  configurations: any[],
  key: string,
  dataType: string,
  defaultValue: T
): T {
  const config = configurations.find((c) => c.key === key);
  if (!config) return defaultValue;

  switch (dataType) {
    case "boolean":
      return (config.value.toLowerCase() === "true") as T;
    case "int":
      return Number.parseInt(config.value, 10) as T;
    case "outcome_tracking_mode":
      return normalizeOutcomeTrackingMode(config.value) as T;
    default:
      return config.value as T;
  }
}

function camelToSnakeCase(str: string): string {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
}

function getDataTypeForKey(key: keyof SalonConfig): string {
  const typeMap: Record<keyof SalonConfig, string> = {
    appointmentWorkerAssignmentEnabled: "boolean",
    outcomeTrackingEnabled: "boolean",
    outcomeTrackingMode: "outcome_tracking_mode",
  };
  return typeMap[key];
}

function serializeValue(value: any, dataType: string): string {
  switch (dataType) {
    case "boolean":
      return value ? "true" : "false";
    case "int":
      return value.toString();
    default:
      return value;
  }
}
